/**
 * Profile Learning Algorithms
 * 
 * Analyzes session history to learn user characteristics:
 * - Recovery capacity and speed
 * - RPE calibration
 * - Progression rate preferences
 * - Volume/intensity response
 */

import type { SessionLog } from './sessionStorage';
import { isPerHandLoad } from './exerciseDatabase';
import type {
  UserAdaptiveProfile,
  RecoveryCapacity,
  RecoverySpeed,
  ResponseLevel,
  RPETendency,
  ProfileAnalysisResult,
  LearningSessionData
} from './types/userProfile';
import { calculateRecoveryDebt } from './recoveryDebt';
import { getRecoveryHistory } from './recoveryCalculator';
import {
  calculateRPECalibration,
  calculateProgressionRate,
  analyzeSleepPerformanceCorrelation,
  detectVolumeOutliers,
  analyzeRecoveryCapacity,
  calculateConfidenceInterval
} from './statisticalAnalysis';

/**
 * Analyze user profile from session history
 * This is the main entry point for profile learning
 */
export async function analyzeUserProfile(
  currentProfile: UserAdaptiveProfile,
  sessions: SessionLog[]
): Promise<ProfileAnalysisResult> {
  const insights: ProfileAnalysisResult['insights'] = [];
  const recommendations: ProfileAnalysisResult['recommendations'] = [];
  const profileUpdates: Partial<UserAdaptiveProfile> = {};
  
  // Need minimum data for meaningful analysis
  if (sessions.length < 3) {
    return {
      profileUpdates: {},
      insights: [{
        category: 'general',
        message: 'Collecting baseline data. Keep training!',
        confidence: 'low',
        actionable: false,
      }],
      recommendations: [],
      confidenceScore: 0.1,
    };
  }
  
  // 1. Analyze Recovery Capacity (using statistical analysis)
  const recoveryData = await getRecoveryHistory();
  const recoveryStats = analyzeRecoveryCapacity(sessions, recoveryData);
  
  if (recoveryStats.confidence > 0.3) {
    profileUpdates.recoveryProfile = {
      ...currentProfile.recoveryProfile,
      capacity: recoveryStats.capacity,
      speed: recoveryStats.speed,
      consistency: recoveryStats.consistency,
      confidence: recoveryStats.confidence,
      lastUpdated: new Date(),
    };
    
    insights.push({
      category: 'recovery',
      message: `Recovery capacity: ${recoveryStats.capacity}, Speed: ${recoveryStats.speed} (${(recoveryStats.consistency * 100).toFixed(0)}% consistent)`,
      confidence: recoveryStats.confidence > 0.7 ? 'high' : recoveryStats.confidence > 0.4 ? 'medium' : 'low',
      actionable: recoveryStats.capacity === 'low',
    });
    
    if (recoveryStats.capacity === 'high') {
      recommendations.push({
        type: 'frequency',
        priority: 'low',
        message: 'Your recovery capacity is excellent. You can handle higher training frequency.',
        reasoning: 'Statistical analysis shows consistent high recovery scores after intense sessions.',
      });
    } else if (recoveryStats.capacity === 'low') {
      recommendations.push({
        type: 'deload',
        priority: 'high',
        message: 'Focus on recovery quality. Consider extra rest days or deload weeks.',
        reasoning: 'Statistical analysis shows lower-than-average recovery scores after training.',
      });
    }
  }
  
  // 2. Calibrate RPE (using statistical analysis)
  const rpeStats = calculateRPECalibration(sessions);
  if (rpeStats.confidence > 0.3) {
    profileUpdates.rpeCalibration = {
      ...currentProfile.rpeCalibration,
      calibrationFactor: rpeStats.calibrationFactor,
      tendency: rpeStats.tendency,
      confidence: rpeStats.confidence,
      sampleSize: sessions.length,
      lastUpdated: new Date(),
    };
    
    insights.push({
      category: 'rpe',
      message: `RPE pattern: ${rpeStats.tendency} (correlation: ${(rpeStats.correlation * 100).toFixed(0)}%)`,
      confidence: rpeStats.confidence > 0.7 ? 'high' : rpeStats.confidence > 0.4 ? 'medium' : 'low',
      actionable: rpeStats.tendency !== 'accurate',
    });
    
    if (rpeStats.tendency === 'conservative') {
      recommendations.push({
        type: 'intensity',
        priority: 'medium',
        message: 'You tend to underestimate effort. System will push you slightly harder.',
        reasoning: 'Statistical analysis shows your reported RPE is consistently lower than calculated intensity.',
      });
    } else if (rpeStats.tendency === 'aggressive') {
      recommendations.push({
        type: 'intensity',
        priority: 'medium',
        message: 'You tend to rate efforts high. System will be more conservative with load increases.',
        reasoning: 'Statistical analysis shows your reported RPE is consistently higher than calculated intensity.',
      });
    }
  }
  
  // 3. Analyze Progression Patterns
  const progressionAnalysis = analyzeProgressionPatterns(sessions);
  if (progressionAnalysis) {
    profileUpdates.progressionProfile = {
      ...currentProfile.progressionProfile,
      ...progressionAnalysis.updates,
      lastUpdated: new Date(),
    };
    insights.push(...progressionAnalysis.insights);
    recommendations.push(...progressionAnalysis.recommendations);
  }
  
  // 4. Detect Volume Outliers (Injury Risk)
  const volumeOutliers = detectVolumeOutliers(sessions);
  if (volumeOutliers.outliers.length > 0) {
    const criticalOutliers = volumeOutliers.outliers.filter(o => o.riskLevel === 'critical' || o.riskLevel === 'high');
    
    if (criticalOutliers.length > 0) {
      insights.push({
        category: 'general',
        message: `${criticalOutliers.length} high-risk volume spike(s) detected in recent training`,
        confidence: 'high',
        actionable: true,
      });
      
      recommendations.push({
        type: 'deload',
        priority: 'high',
        message: 'Recent volume spikes detected. Consider deload or reducing volume by 20-30%.',
        reasoning: `Statistical analysis shows ${criticalOutliers.length} session(s) with volume >2 standard deviations above your baseline.`,
      });
    }
  }
  
  // 5. Analyze Sleep-Performance Correlation
  const sleepCorrelation = analyzeSleepPerformanceCorrelation(sessions, recoveryData);
  if (sleepCorrelation.confidence > 0.3) {
    insights.push({
      category: 'recovery',
      message: `Sleep-performance correlation: ${(sleepCorrelation.correlation * 100).toFixed(0)}%. Optimal: ${sleepCorrelation.optimalSleepHours}h`,
      confidence: sleepCorrelation.confidence > 0.6 ? 'high' : 'medium',
      actionable: sleepCorrelation.correlation > 0.3,
    });
    
    if (sleepCorrelation.correlation > 0.4) {
      recommendations.push({
        type: 'frequency',
        priority: 'medium',
        message: `Target ${sleepCorrelation.optimalSleepHours} hours of sleep. Each hour under reduces performance by ~${sleepCorrelation.performanceDropPerHour.toFixed(0)}%.`,
        reasoning: 'Statistical analysis shows significant correlation between sleep and training performance.',
      });
    }
  }
  
  // 6. Update Data Quality
  const dataQuality = assessDataQuality(sessions, currentProfile.createdAt);
  profileUpdates.dataQuality = {
    ...dataQuality,
    lastAssessed: new Date(),
  };
  
  // 5. Update Performance Metrics
  profileUpdates.performanceMetrics = updatePerformanceMetrics(
    currentProfile.performanceMetrics,
    sessions
  );
  
  // Calculate overall confidence
  const confidenceScore = calculateOverallConfidence(
    recoveryStats.confidence || 0,
    rpeStats.confidence || 0,
    progressionAnalysis?.confidence || 0,
    dataQuality.consistencyScore
  );
  
  return {
    profileUpdates,
    insights,
    recommendations,
    confidenceScore,
  };
}

/**
 * Analyze recovery capacity from session patterns (legacy function - kept for compatibility)
 */
function analyzeRecoveryCapacityLegacy(sessions: SessionLog[]): {
  updates: Partial<UserAdaptiveProfile['recoveryProfile']>;
  insights: ProfileAnalysisResult['insights'];
  confidence: number;
} | null {
  // Filter PT sessions only
  const ptSessions = sessions.filter(s => s.sessionType === 'pt' || s.sessionType === 'assessment');
  if (ptSessions.length < 5) return null;
  
  // Calculate average sessions per week
  const firstSession = new Date(ptSessions[0].date);
  const lastSession = new Date(ptSessions[ptSessions.length - 1].date);
  const weeksDiff = (lastSession.getTime() - firstSession.getTime()) / (7 * 24 * 60 * 60 * 1000);
  const sessionsPerWeek = ptSessions.length / Math.max(weeksDiff, 1);
  
  // Calculate average RPE
  const avgRPE = ptSessions.reduce((sum, s) => sum + (s.overallFatigue || 7), 0) / ptSessions.length;
  
  // Determine capacity based on frequency and RPE maintenance
  let capacity: RecoveryCapacity = 'average';
  let speed: RecoverySpeed = 'average';
  let confidence = 0;
  
  if (sessionsPerWeek >= 4 && avgRPE < 7.5) {
    capacity = 'high';
    speed = 'fast';
    confidence = 0.7;
  } else if (sessionsPerWeek >= 3 && avgRPE < 8) {
    capacity = 'average';
    speed = 'average';
    confidence = 0.6;
  } else if (sessionsPerWeek < 3 || avgRPE > 8) {
    capacity = 'low';
    speed = 'slow';
    confidence = 0.5;
  }
  
  // Calculate baseline readiness
  const readinessScores = ptSessions
    .map(s => s.readiness)
    .filter(r => r > 0);
  const baselineReadiness = readinessScores.length > 0
    ? readinessScores.reduce((sum, r) => sum + r, 0) / readinessScores.length
    : 70;
  
  const insights: ProfileAnalysisResult['insights'] = [{
    category: 'recovery',
    message: `Recovery capacity: ${capacity}. Training ${sessionsPerWeek.toFixed(1)}x/week with avg RPE ${avgRPE.toFixed(1)}.`,
    confidence: confidence > 0.6 ? 'high' : 'medium',
    actionable: true,
  }];
  
  return {
    updates: {
      capacity,
      speed,
      confidence,
      baselineReadiness: Math.round(baselineReadiness),
      optimalRestDays: capacity === 'high' ? 1 : capacity === 'average' ? 1.5 : 2,
    },
    insights,
    confidence,
  };
}

/**
 * Calibrate user's RPE reporting against objective markers
 */
function calibrateRPE(sessions: SessionLog[]): {
  updates: Partial<UserAdaptiveProfile['rpeCalibration']>;
  insights: ProfileAnalysisResult['insights'];
  confidence: number;
} | null {
  const ptSessions = sessions.filter(s => s.sessionType === 'pt' && 'exercises' in s);
  if (ptSessions.length < 8) return null;
  
  // Analyze progression rate vs reported RPE
  let totalProgression = 0;
  let totalRPE = 0;
  let progressionCount = 0;
  
  for (let i = 1; i < ptSessions.length; i++) {
    const prevSession = ptSessions[i - 1] as any;
    const currSession = ptSessions[i] as any;
    
    if (!prevSession.exercises || !currSession.exercises) continue;
    
    // Find common exercises
    prevSession.exercises.forEach((prevEx: any) => {
      const currEx = currSession.exercises.find((e: any) => e.exerciseName === prevEx.exerciseName);
      if (!currEx) return;
      
      // Calculate progression
      const prevWeight = prevEx.sets?.[0]?.weight || prevEx.actualLoad || 0;
      const currWeight = currEx.sets?.[0]?.weight || currEx.actualLoad || 0;
      
      if (prevWeight > 0 && currWeight > 0) {
        const progression = (currWeight - prevWeight) / prevWeight;
        const rpe = prevEx.sets?.[0]?.rpe || prevEx.actualRPE || 7;
        
        totalProgression += progression;
        totalRPE += rpe;
        progressionCount++;
      }
    });
  }
  
  if (progressionCount < 5) return null;
  
  const avgProgression = totalProgression / progressionCount;
  const avgRPE = totalRPE / progressionCount;
  
  // Determine tendency
  let tendency: RPETendency = 'accurate';
  let calibrationFactor = 1.0;
  let confidence = 0.6;
  
  // If progressing well (>4%/session) with low RPE (<7.5), user is conservative
  if (avgProgression > 0.04 && avgRPE < 7.5) {
    tendency = 'conservative';
    calibrationFactor = 1.1;
    confidence = 0.7;
  }
  // If struggling to progress (<2%/session) with high RPE (>7.5), user is aggressive
  else if (avgProgression < 0.02 && avgRPE > 7.5) {
    tendency = 'aggressive';
    calibrationFactor = 0.9;
    confidence = 0.7;
  }
  
  const insights: ProfileAnalysisResult['insights'] = [{
    category: 'rpe',
    message: `RPE calibration: ${tendency}. Avg progression ${(avgProgression * 100).toFixed(1)}%/session at RPE ${avgRPE.toFixed(1)}.`,
    confidence: confidence > 0.65 ? 'high' : 'medium',
    actionable: true,
  }];
  
  return {
    updates: {
      tendency,
      calibrationFactor,
      confidence,
    },
    insights,
    confidence,
  };
}

/**
 * Analyze progression patterns and volume/intensity response
 */
function analyzeProgressionPatterns(sessions: SessionLog[]): {
  updates: Partial<UserAdaptiveProfile['progressionProfile']>;
  insights: ProfileAnalysisResult['insights'];
  recommendations: ProfileAnalysisResult['recommendations'];
  confidence: number;
} | null {
  const ptSessions = sessions.filter(s => s.sessionType === 'pt' && 'exercises' in s);
  if (ptSessions.length < 10) return null;
  
  // Calculate average progression rate
  let totalProgressionRate = 0;
  let progressionSamples = 0;
  
  for (let i = 1; i < ptSessions.length; i++) {
    const prevSession = ptSessions[i - 1] as any;
    const currSession = ptSessions[i] as any;
    
    if (!prevSession.exercises || !currSession.exercises) continue;
    
    prevSession.exercises.forEach((prevEx: any) => {
      const currEx = currSession.exercises.find((e: any) => e.exerciseName === prevEx.exerciseName);
      if (!currEx) return;
      
      const prevWeight = prevEx.sets?.[0]?.weight || prevEx.actualLoad || 0;
      const currWeight = currEx.sets?.[0]?.weight || currEx.actualLoad || 0;
      
      if (prevWeight > 0 && currWeight > prevWeight) {
        const rate = (currWeight - prevWeight) / prevWeight;
        totalProgressionRate += rate;
        progressionSamples++;
      }
    });
  }
  
  if (progressionSamples < 5) return null;
  
  const preferredProgressionRate = totalProgressionRate / progressionSamples;
  const confidence = Math.min(0.8, progressionSamples / 20);
  
  // Determine volume vs intensity response (simplified for now)
  const responseToVolume: ResponseLevel = 'moderate';
  const responseToIntensity: ResponseLevel = 'moderate';
  
  // Calculate optimal frequency
  const daysBetweenSessions: number[] = [];
  for (let i = 1; i < ptSessions.length; i++) {
    const prevDate = new Date(ptSessions[i - 1].date);
    const currDate = new Date(ptSessions[i].date);
    const days = (currDate.getTime() - prevDate.getTime()) / (24 * 60 * 60 * 1000);
    daysBetweenSessions.push(days);
  }
  const avgDaysBetween = daysBetweenSessions.reduce((sum, d) => sum + d, 0) / daysBetweenSessions.length;
  const optimalFrequency = 7 / avgDaysBetween;
  
  const insights: ProfileAnalysisResult['insights'] = [{
    category: 'progression',
    message: `Optimal progression rate: ${(preferredProgressionRate * 100).toFixed(1)}%/session. Training ${optimalFrequency.toFixed(1)}x/week.`,
    confidence: confidence > 0.6 ? 'high' : 'medium',
    actionable: true,
  }];
  
  const recommendations: ProfileAnalysisResult['recommendations'] = [];
  
  if (preferredProgressionRate < 0.02) {
    recommendations.push({
      type: 'progression',
      message: 'Progression rate is slow. Consider increasing volume or intensity.',
      priority: 'medium',
      reasoning: 'Average progression is below 2% per session',
    });
  }
  
  return {
    updates: {
      responseToVolume,
      responseToIntensity,
      optimalFrequency,
      preferredProgressionRate,
      confidence,
    },
    insights,
    recommendations,
    confidence,
  };
}

/**
 * Assess data quality and consistency
 */
function assessDataQuality(sessions: SessionLog[], profileCreatedAt: Date): UserAdaptiveProfile['dataQuality'] {
  const now = new Date();
  const timeInSystem = Math.floor((now.getTime() - profileCreatedAt.getTime()) / (24 * 60 * 60 * 1000));
  
  // Calculate consistency score based on tracking completeness
  const ptSessions = sessions.filter(s => s.sessionType === 'pt' && 'exercises' in s);
  const completeSessions = ptSessions.filter(s => {
    const exercises = (s as any).exercises || [];
    return exercises.length > 0 && exercises.every((ex: any) => ex.completed);
  });
  
  const consistencyScore = ptSessions.length > 0 
    ? completeSessions.length / ptSessions.length 
    : 0;
  
  let overallConfidence: 'low' | 'medium' | 'high' = 'low';
  if (sessions.length >= 20 && consistencyScore > 0.8) {
    overallConfidence = 'high';
  } else if (sessions.length >= 10 && consistencyScore > 0.6) {
    overallConfidence = 'medium';
  }
  
  return {
    sessionsTracked: sessions.length,
    consistencyScore,
    timeInSystem,
    overallConfidence,
    lastAssessed: now,
  };
}

/**
 * Update performance metrics from sessions
 */
function updatePerformanceMetrics(
  current: UserAdaptiveProfile['performanceMetrics'],
  sessions: SessionLog[]
): UserAdaptiveProfile['performanceMetrics'] {
  const ptSessions = sessions.filter(s => s.sessionType === 'pt' && 'exercises' in s);
  
  // Calculate total volume
  let totalVolume = 0;
  ptSessions.forEach(session => {
    const exercises = (session as any).exercises || [];
    exercises.forEach((ex: any) => {
      if (ex.sets) {
        ex.sets.forEach((set: any) => {
          // Inline tonnage (per-hand/unilateral aware) — matches computeSetVolume
          // semantics. Inlined to avoid a circular import
          // (volumeTracking → sessionStorage → profileLearning).
          const reps = set.reps || 0;
          const perHandMult = set.unilateral
            ? 1
            : ((typeof set.perHand === 'boolean' ? set.perHand : isPerHandLoad(ex.exerciseName)) ? 2 : 1);
          const totalReps = set.unilateral
            ? reps + (typeof set.rightReps === 'number' ? set.rightReps : reps)
            : reps;
          totalVolume += (set.weight || 0) * perHandMult * totalReps;
        });
      }
    });
  });
  
  // Calculate average session RPE
  const avgSessionRPE = ptSessions.length > 0
    ? ptSessions.reduce((sum, s) => sum + (s.overallFatigue || 7), 0) / ptSessions.length
    : 7;
  
  return {
    ...current,
    totalSessions: sessions.length,
    totalVolume,
    averageSessionRPE: avgSessionRPE,
  };
}

/**
 * Calculate overall profile confidence
 */
function calculateOverallConfidence(
  recoveryConfidence: number,
  rpeConfidence: number,
  progressionConfidence: number,
  dataQuality: number
): number {
  const weights = {
    recovery: 0.25,
    rpe: 0.20,
    progression: 0.25,
    dataQuality: 0.30,
  };
  
  return (
    recoveryConfidence * weights.recovery +
    rpeConfidence * weights.rpe +
    progressionConfidence * weights.progression +
    dataQuality * weights.dataQuality
  );
}

/**
 * Initialize ML profile for new user during onboarding
 * Called for ALL users (program or custom workout path)
 * 
 * @param userId - Firebase user ID
 * @param essentialData - Minimum data needed for ML (age, gender, bodyWeight)
 * @returns Initialized UserAdaptiveProfile
 */
export function initializeUserProfile(
  userId: string,
  essentialData: {
    age: number;
    gender: 'male' | 'female' | 'other';
    bodyWeight: number;
  }
): UserAdaptiveProfile {
  const now = new Date();
  
  return {
    userId,
    createdAt: now,
    lastUpdated: now,
    version: 1,
    
    // Initialize recovery profile with age-based defaults
    recoveryProfile: {
      capacity: 'average',
      speed: 'average',
      consistency: 0.5,
      confidence: 0.1, // Low confidence initially
      baselineReadiness: 70,
      optimalRestDays: estimateOptimalRestDays(essentialData.age),
      lastUpdated: now,
    },
    
    // Initialize progression profile with conservative defaults
    progressionProfile: {
      responseToVolume: 'moderate',
      responseToIntensity: 'moderate',
      optimalFrequency: 4, // Default 4x/week
      preferredProgressionRate: 0.025, // 2.5% per session
      confidence: 0.1,
      lastUpdated: now,
    },
    
    // Initialize RPE calibration (assume accurate until proven otherwise)
    rpeCalibration: {
      tendency: 'accurate',
      calibrationFactor: 1.0,
      confidence: 0.1,
      sampleSize: 0,
      lastUpdated: now,
    },
    
    // Initialize fitness-fatigue model
    fitnessModel: {
      currentFitness: 50, // Neutral starting point
      currentFatigue: 0,
      readiness: 50,
      fitnessDecayRate: 15,
      fatigueDecayRate: 3,
      lastCalculated: now,
    },
    
    // Initialize performance metrics
    performanceMetrics: {
      totalSessions: 0,
      totalVolume: 0,
      averageSessionRPE: 7,
      deloadFrequency: 4, // Every 4 weeks
      injuryHistory: [],
      personalRecords: [],
    },
    
    // Initialize data quality
    dataQuality: {
      sessionsTracked: 0,
      consistencyScore: 0,
      timeInSystem: 0,
      overallConfidence: 'low',
      lastAssessed: now,
    },
    
    // Enable learning by default
    learningEnabled: true,
    lastAnalysisDate: now,
    nextScheduledAnalysis: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000), // 7 days
  };
}

/**
 * Estimate optimal rest days based on age
 * Younger athletes recover faster, older athletes need more recovery
 * 
 * @param age - User's age in years
 * @returns Estimated optimal rest days between sessions
 */
function estimateOptimalRestDays(age: number): number {
  if (age < 25) return 1; // Young athletes recover faster
  if (age < 35) return 1.5;
  if (age < 45) return 2;
  if (age < 55) return 2.5;
  return 3; // Older athletes need more recovery
}

